import type { FilteredProductItem } from "@nwt/norce-pwa/dist/runtime/types/api/searchAndFilter/filters";
import type {
	ParametricListInfo,
	ParametricListValue
} from "@nwt/norce-pwa/dist/runtime/types/api/product/product";
import type { GroupedProductWithVariants } from "~/utils/product/groupVariantsByKey";
import { parametricsSeedToQuery } from "~/utils/parametricsUrl";

const DEFAULT_COLOR = "#ccc";

/**
 * Parse ParametricListSeed to extract parametric values
 * @param seed - Format: "10:19,1:1,2:11" where first number is ParametricId, second is ValueId
 * @returns Array of parametric objects with parametricId and valueId
 */
export function parseParametricListSeed(
	seed?: string
): Array<{ parametricId: number; valueId: number }> {
	if (!seed) return [];
	return seed.split(",").map((item) => {
		const [parametricId, valueId] = item.split(":");
		return {
			parametricId: parseInt(parametricId!, 10),
			valueId: parseInt(valueId!, 10)
		};
	});
}

function getParsedParametrics(variant: FilteredProductItem) {
	return parseParametricListSeed(variant.ParametricListSeed);
}

/**
 * Parse `VariantParametricSeed` ("1,2") into a list of parametric group ids —
 * the groups that make a product a distinct variant (e.g. colour + size).
 */
export function parseVariantParametricSeed(seed?: string): number[] {
	if (!seed) return [];
	return seed
		.split(",")
		.map((id) => parseInt(id, 10))
		.filter((id) => !Number.isNaN(id));
}

/**
 * Resolve the given parametric groups of a variant to readable value names.
 *
 * `ParametricListSeed` carries the `groupId:valueId` pairs; only the requested
 * `groupIds` are resolved (sorted by group id for a stable order) via the
 * parametric value catalog, returning e.g. `["Red", "Small"]`. Returns an empty
 * array when nothing resolves (no groups, no catalog, or no matching values),
 * so callers can fall back to the product name.
 *
 * Pass the relation-wide set of variant groups (not each item's own
 * `VariantParametricSeed`) so non-variant products added to a relation — which
 * carry parametric values but declare no variant groups — still resolve the
 * same parametrics as the real variants.
 */
export function getVariantParametricValues(
	variant: { ParametricListSeed?: string },
	groupIds: Iterable<number>,
	allParametrics: ParametricListValue[]
): string[] {
	const groups = new Set(groupIds);
	if (groups.size === 0 || allParametrics.length === 0) return [];

	return parseParametricListSeed(variant.ParametricListSeed)
		.filter((pair) => groups.has(pair.parametricId))
		.sort((a, b) => a.parametricId - b.parametricId)
		.map((pair) => {
			const match = allParametrics.find(
				(p) => p.ParametricId === pair.parametricId && p.Id === pair.valueId
			);
			return match?.Name || match?.Code || "";
		})
		.filter(Boolean);
}

/**
 * Parse ParametricMultipleSeed to extract allowed color values (Code or Name)
 * @param seed - Format: "5:3,5:5,5:8" where first number is ParametricId, second is ValueId
 * @param colorParametricId - Color parametric ID to filter by
 * @param allParametrics - All available parametric values to resolve Code/Name
 * @returns Array of allowed color values (Code or Name)
 */
export function parseParametricMultipleSeed(
	seed?: string,
	colorParametricId?: number | null,
	allParametrics?: ParametricListValue[]
): string[] {
	if (
		!seed ||
		!colorParametricId ||
		!allParametrics ||
		allParametrics.length === 0
	)
		return [];

	const valueMap = new Map<
		number,
		{ code: string | null; name: string | null }
	>();
	for (const p of allParametrics) {
		if (p.ParametricId === colorParametricId) {
			valueMap.set(p.Id, {
				code: p.Code || null,
				name: p.Name || null
			});
		}
	}

	const parsed = parseParametricListSeed(seed);
	const colorEntries = parsed.filter(
		(p) => p.parametricId === colorParametricId
	);

	return colorEntries
		.map((entry) => {
			const valueData = valueMap.get(entry.valueId);
			return valueData?.code || valueData?.name || null;
		})
		.filter((value): value is string => value !== null);
}

/**
 * Find color parametric ID from parametrics information (for swatches, variants, etc.)
 * Uses standard color keys: color, färg, variantcolor, variantfärg
 * @param parametricsInfo - Array of parametric information
 * @returns Color parametric ID if found, null otherwise
 */
export function findColorParametricId(
	parametricsInfo: ParametricListInfo[]
): number | null {
	const colorKeys = ["color", "färg", "variantcolor", "variantfärg"];

	const colorParam = parametricsInfo?.find(
		(p) =>
			colorKeys.includes((p.Code || "").toLowerCase()) ||
			colorKeys.includes((p.Name || "").toLowerCase())
	);

	return colorParam?.Id ?? null;
}

/**
 * Resolve the color parametric *value* a variant points at, so callers can read
 * more than its color string — `ImageKey` in particular, for values that carry
 * an uploaded swatch image (a "Striped" pattern has no meaningful hex code).
 * @param variant - Product variant
 * @param colorParametricId - Color parametric ID
 * @param allParametrics - All available parametric values
 * @returns The matching parametric value, or undefined when the variant has no
 * color parametric or the value is missing from the catalog
 */
export function getColorParametricValue(
	variant: FilteredProductItem,
	colorParametricId: number | null,
	allParametrics: ParametricListValue[]
): ParametricListValue | undefined {
	if (!colorParametricId || !allParametrics?.length) {
		return undefined;
	}

	const colorParam = getParsedParametrics(variant).find(
		(p) => p.parametricId === colorParametricId
	);

	if (!colorParam) {
		return undefined;
	}

	return allParametrics.find(
		(p) =>
			p.ParametricId === colorParam.parametricId && p.Id === colorParam.valueId
	);
}

/**
 * Get color value from variant (from parametric or fallback)
 * @param variant - Product variant
 * @param colorParametricId - Color parametric ID
 * @param allParametrics - All available parametric values
 * @returns Color code/description or default color
 */
export function getColorValue(
	variant: FilteredProductItem,
	colorParametricId: number | null,
	allParametrics: ParametricListValue[]
): string {
	const colorValue = getColorParametricValue(
		variant,
		colorParametricId,
		allParametrics
	);

	return String(colorValue?.Code ?? colorValue?.Description ?? DEFAULT_COLOR);
}

/**
 * Get color value Code or Name from variant
 * @param variant - Product variant
 * @param colorParametricId - Color parametric ID
 * @param allParametrics - All available parametric values
 * @param colorValueMap - Optional pre-built Map for O(1) lookup (for performance)
 * @returns Color Code or Name, or null if not found
 */
export function getColorValueCodeOrName(
	variant: FilteredProductItem,
	colorParametricId: number | null,
	allParametrics: ParametricListValue[],
	colorValueMap?: Map<number, { code: string | null; name: string | null }>
): string | null {
	if (!colorParametricId || !allParametrics?.length) {
		return null;
	}

	const parametrics = getParsedParametrics(variant);
	const colorParam = parametrics.find(
		(p) => p.parametricId === colorParametricId
	);

	if (!colorParam) {
		return null;
	}

	// Use Map if provided for O(1) lookup, otherwise fallback to find()
	if (colorValueMap) {
		const valueData = colorValueMap.get(colorParam.valueId);
		return valueData?.code || valueData?.name || null;
	}

	const colorValue = allParametrics.find(
		(p) =>
			p.ParametricId === colorParam.parametricId && p.Id === colorParam.valueId
	);

	return colorValue?.Code || colorValue?.Name || null;
}

/**
 * Check if variant is a color variant
 * @param variant - Product variant
 * @param colorParametricId - Color parametric ID
 * @returns True if variant has color parametric
 */
export function isColorVariant(
	variant: FilteredProductItem,
	colorParametricId: number | null
): boolean {
	if (!colorParametricId) return false;
	const parametrics = getParsedParametrics(variant);
	return parametrics.some((p) => p.parametricId === colorParametricId);
}

/**
 * Get label for non-color variants
 * @param variant - Product variant
 * @param colorParametricId - Color parametric ID to exclude
 * @param allParametrics - All available parametric values
 * @returns Variant label (Name preferred, then Code, VariantName, or PartNo)
 */
export function getVariantLabel(
	variant: FilteredProductItem,
	colorParametricId: number | null,
	allParametrics: ParametricListValue[]
): string {
	const parametrics = getParsedParametrics(variant);

	const nonColorParam = parametrics.find(
		(p) => p.parametricId !== colorParametricId
	);

	if (nonColorParam && allParametrics.length > 0) {
		const paramValue = allParametrics.find(
			(p) =>
				p.ParametricId === nonColorParam.parametricId &&
				p.Id === nonColorParam.valueId
		);
		if (paramValue) {
			return (
				paramValue.Name ||
				paramValue.Code ||
				variant.VariantName ||
				variant.Name
			);
		}
	}

	return variant.VariantName || variant.Name || variant.PartNo;
}

/**
 * Check if variant is in stock
 * @param variant - Product variant
 * @returns True if variant has stock (OnHand or OnHandStore > 0)
 */
export function isInStock(variant: FilteredProductItem): boolean {
	return Boolean(
		(variant.OnHand?.Value ?? 0) > 0 || (variant.OnHandStore?.Value ?? 0) > 0
	);
}

/**
 * Get route for variant product (path string or { path, query } for slug parametrics).
 * @param variant - Product variant
 * @param localePath - Locale path function
 * @param allParametrics - Optional; when provided, builds slug query (?color=white&size=xs)
 */
export function getVariantRoute(
	variant: FilteredProductItem,
	localePath: (path: string) => string,
	allParametrics?: ParametricListValue[]
): string | { path: string; query: Record<string, string> } {
	const productName = variant?.VariantUniqueName || variant?.UniqueName;

	if (!productName) {
		return localePath(`/product/${variant?.Id}`);
	}

	const path = localePath(`/${productName}`);
	if (!variant?.ParametricListSeed || !variant?.VariantParametricSeed?.length) {
		return path;
	}
	if (allParametrics?.length) {
		const query = parametricsSeedToQuery(
			variant.ParametricListSeed,
			allParametrics
		);
		return Object.keys(query).length ? { path, query } : path;
	}
	return path;
}

/**
 * Get first non-color parametric ID from variant
 * @param variant - Product variant
 * @param colorParametricId - Color parametric ID to exclude
 * @returns First non-color parametric ID or null
 */
export function getFirstNonColorParametricId(
	variant: FilteredProductItem,
	colorParametricId: number | null
): number | null {
	if (!colorParametricId) return null;

	const parametrics = getParsedParametrics(variant);
	const nonColorParam = parametrics.find(
		(p) => p.parametricId !== colorParametricId
	);

	return nonColorParam ? nonColorParam.parametricId : null;
}

/**
 * Create a Map for fast lookup of parametric values by valueId
 * @param parametricId - Parametric ID to filter by
 * @param allParametrics - All available parametric values
 * @returns Map<valueId, {code: string | null, name: string | null}>
 */
function createParametricValueMap(
	parametricId: number,
	allParametrics: ParametricListValue[]
): Map<number, { code: string | null; name: string | null }> {
	const valueMap = new Map<
		number,
		{ code: string | null; name: string | null }
	>();

	for (const param of allParametrics) {
		if (param.ParametricId === parametricId) {
			valueMap.set(param.Id, {
				code: param.Code || null,
				name: param.Name || null
			});
		}
	}

	return valueMap;
}

/**
 * Get unique variants grouped by parametric valueId (prioritizing in-stock variants)
 * Universal function for grouping variants by any parametric
 * @param variants - Array of product variants
 * @param parametricId - Parametric ID to group by
 * @param prioritizeInStock - Whether to prioritize in-stock variants
 * @returns One variant per unique valueId, prioritizing in-stock if enabled
 */
function getUniqueVariantsByParametric(
	variants: FilteredProductItem[],
	parametricId: number | null,
	prioritizeInStock: boolean = true,
	allowedValueIds?: number[],
	allowedValues?: string[],
	allParametrics?: ParametricListValue[]
): FilteredProductItem[] {
	if (!parametricId || !variants || variants.length === 0) {
		return [];
	}

	// Create Map for O(1) lookup instead of O(m) find() calls
	const valueMap =
		allParametrics && allParametrics.length > 0
			? createParametricValueMap(parametricId, allParametrics)
			: null;

	const variantsByValueId = new Map<number, FilteredProductItem[]>();

	for (const variant of variants) {
		const parametrics = getParsedParametrics(variant);
		const param = parametrics.find((p) => p.parametricId === parametricId);

		if (param) {
			const valueId = param.valueId;

			if (
				allowedValueIds &&
				allowedValueIds.length > 0 &&
				!allowedValueIds.includes(valueId)
			) {
				continue;
			}

			if (allowedValues && allowedValues.length > 0 && valueMap) {
				const valueData = valueMap.get(valueId);
				const allowedByCode =
					valueData?.code && allowedValues.includes(valueData.code);
				const allowedByName =
					valueData?.name && allowedValues.includes(valueData.name);
				if (!allowedByCode && !allowedByName) {
					continue;
				}
			}

			if (!variantsByValueId.has(valueId)) {
				variantsByValueId.set(valueId, []);
			}
			variantsByValueId.get(valueId)!.push(variant);
		}
	}

	const uniqueVariants: FilteredProductItem[] = [];

	for (const [_, groupVariants] of variantsByValueId.entries()) {
		let selectedVariant: FilteredProductItem | undefined;

		if (prioritizeInStock) {
			const sorted = [...groupVariants].sort((a, b) => {
				const aInStock = isInStock(a);
				const bInStock = isInStock(b);
				return Number(bInStock) - Number(aInStock);
			});
			selectedVariant = sorted[0];
		} else {
			selectedVariant = groupVariants[0];
		}

		if (selectedVariant) {
			uniqueVariants.push(selectedVariant);
		}
	}

	return uniqueVariants;
}

/**
 * Get unique color variants (one per color valueId, prioritizing in-stock variants)
 * @param variants - Array of product variants
 * @param colorParametricId - Color parametric ID
 * @param allowedColorValueIds - Optional array of allowed color valueIds to filter by
 * @param allowedColorValues - Optional array of allowed color values (Code/Name) to filter by
 * @param allParametrics - All available parametric values (required if using allowedColorValues)
 * @returns One variant per unique color valueId
 */
export function getUniqueColorVariants(
	variants: FilteredProductItem[],
	colorParametricId: number | null,
	allowedColorValueIds?: number[],
	allowedColorValues?: string[],
	allParametrics?: ParametricListValue[]
): FilteredProductItem[] {
	return getUniqueVariantsByParametric(
		variants,
		colorParametricId,
		true,
		allowedColorValueIds,
		allowedColorValues,
		allParametrics
	);
}

/**
 * Get filtered color variants based on ParametricMultipleSeed
 * @param variants - Array of product variants
 * @param colorParametricId - Color parametric ID
 * @param parametricMultipleSeed - ParametricMultipleSeed string from product/variant
 * @param allParametrics - All available parametric values
 * @returns Filtered unique color variants based on ParametricMultipleSeed, or all unique colors if seed is missing
 */
export function getFilteredColorVariants(
	variants: FilteredProductItem[],
	colorParametricId: number | null,
	parametricMultipleSeed?: string,
	allParametrics?: ParametricListValue[]
): FilteredProductItem[] {
	const allowedColorValues = parseParametricMultipleSeed(
		parametricMultipleSeed,
		colorParametricId,
		allParametrics
	);
	return getUniqueColorVariants(
		variants,
		colorParametricId,
		undefined,
		allowedColorValues.length > 0 ? allowedColorValues : undefined,
		allParametrics
	);
}

/**
 * Process group colors: parse ParametricMultipleSeed, resolve colors, build unique colors array
 * Optimized version that parses ParametricMultipleSeed once per group
 * @param grouped - Grouped product with variants
 * @param allowedColorsParametricId - Parametric ID for allowed/visible products (from ParametricMultipleSeed)
 * @param colorParametricId - Color parametric ID for variant detection and swatches
 * @param allParametrics - All available parametric values
 * @returns Object with allowedColorValues (Code/Name array) and uniqueColorVariants
 */
export function processGroupColors(
	grouped: GroupedProductWithVariants,
	allowedColorsParametricId: number | null,
	colorParametricId: number | null,
	allParametrics: ParametricListValue[]
): {
	allowedColorValues: string[];
	uniqueColorVariants: FilteredProductItem[];
} {
	const allVariantsInGroup = grouped.product
		? [grouped.product, ...grouped.variants]
		: grouped.variants;

	if (!colorParametricId || allVariantsInGroup.length === 0) {
		return {
			allowedColorValues: [],
			uniqueColorVariants: []
		};
	}

	// Get ParametricMultipleSeed from product or first variant (once per group)
	const parametricMultipleSeed =
		grouped.product?.ParametricMultipleSeed ||
		allVariantsInGroup.find((v) => v.ParametricMultipleSeed)
			?.ParametricMultipleSeed;

	// Parse and resolve colors to Code/Name using allowedColors parametric (visible products)
	const allowedColorValues = allowedColorsParametricId
		? parseParametricMultipleSeed(
				parametricMultipleSeed,
				allowedColorsParametricId,
				allParametrics
			)
		: [];

	// Build unique colors array efficiently using colorParametricId (for variant detection)
	const uniqueColorVariants = getUniqueColorVariants(
		allVariantsInGroup,
		colorParametricId,
		undefined,
		allowedColorValues.length > 0 ? allowedColorValues : undefined,
		allParametrics
	);

	return {
		allowedColorValues,
		uniqueColorVariants
	};
}

/**
 * Get unique other variants (one per non-color parametric valueId, prioritizing in-stock variants)
 * @param variants - Array of product variants
 * @param colorParametricId - Color parametric ID to exclude
 * @returns One variant per unique non-color parametric valueId, or all variants if no non-color parametric found
 */
export function getUniqueOtherVariants(
	variants: FilteredProductItem[],
	colorParametricId: number | null
): FilteredProductItem[] {
	if (!variants || variants.length === 0) {
		return [];
	}

	const firstVariant = variants[0];
	const firstNonColorParamId = getFirstNonColorParametricId(
		firstVariant!,
		colorParametricId
	);

	if (!firstNonColorParamId) {
		return variants;
	}

	return getUniqueVariantsByParametric(variants, firstNonColorParamId, true);
}

/**
 * Find variant matching selected color and size parametrics
 * @param variants - Array of all product variants
 * @param colorParametricId - Color parametric ID
 * @param selectedColorValueId - Selected color value ID
 * @param sizeParametricId - Size/other parametric ID (optional)
 * @param selectedSizeValueId - Selected size value ID (optional)
 * @returns Matching variant or null if not found
 */
export function findMatchingVariant(
	variants: FilteredProductItem[],
	colorParametricId: number | null,
	selectedColorValueId: number | null,
	sizeParametricId: number | null,
	selectedSizeValueId: number | null
): FilteredProductItem | null {
	if (!variants || variants.length === 0) {
		return null;
	}

	for (const variant of variants) {
		const parametrics = getParsedParametrics(variant);
		let matchesColor = true;
		let matchesSize = true;

		if (colorParametricId && selectedColorValueId !== null) {
			const colorParam = parametrics.find(
				(p) => p.parametricId === colorParametricId
			);
			matchesColor = colorParam?.valueId === selectedColorValueId;
		}

		if (sizeParametricId && selectedSizeValueId !== null) {
			const sizeParam = parametrics.find(
				(p) => p.parametricId === sizeParametricId
			);
			matchesSize = sizeParam?.valueId === selectedSizeValueId;
		}

		if (matchesColor && matchesSize) {
			return variant;
		}
	}

	return null;
}

/**
 * Get color value ID from variant
 * @param variant - Product variant
 * @param colorParametricId - Color parametric ID
 * @returns Color value ID or null
 */
export function getColorValueId(
	variant: FilteredProductItem,
	colorParametricId: number | null
): number | null {
	if (!colorParametricId) return null;
	const parametrics = getParsedParametrics(variant);
	const colorParam = parametrics.find(
		(p) => p.parametricId === colorParametricId
	);
	return colorParam?.valueId ?? null;
}

/**
 * Get size/other parametric value ID from variant
 * @param variant - Product variant
 * @param sizeParametricId - Size parametric ID
 * @returns Size value ID or null
 */
export function getSizeValueId(
	variant: FilteredProductItem,
	sizeParametricId: number | null
): number | null {
	if (!sizeParametricId) return null;
	const parametrics = getParsedParametrics(variant);
	const sizeParam = parametrics.find(
		(p) => p.parametricId === sizeParametricId
	);
	return sizeParam?.valueId ?? null;
}
